import { Text, Box, Stack } from "@mantine/core";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import { TInsuranceAsset } from "@/hooks/api/useGetInsuranceById";
import { InsuranceAssetList } from "./insuranceAsset";
import { AllocationBarChart } from "../allocationBarChart";
import { allocationChartColor } from "../../utils/allocationChartColors";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const isMobile = isMobileDevice();

const InsuranceAssetOverview = ({ assets }: { assets: TInsuranceAsset[] }) => {
  const totalMarketValue = assets.reduce(
    (sum, asset) => sum + asset.marketValue,
    0,
  );

  const chartAssets =
    assets
      .filter((asset) => asset.marketValue > 0)
      .sort((a, b) => b.marketValue - a.marketValue)
      .map((asset, i) => ({
        name: asset.name,
        value: asset.marketValue,
        bbType: asset.bbType,
        bbName: asset.bbName,
        share: (asset.marketValue / totalMarketValue) * 100,
        color: allocationChartColor(i),
      })) || [];

  const groupedByBbType = Object.entries(
    chartAssets.reduce(
      (acc, asset) => {
        if (!acc[asset.bbType]) {
          acc[asset.bbType] = [];
        }
        acc[asset.bbType]!.push(asset);
        return acc;
      },
      {} as Record<string, typeof chartAssets>,
    ),
  ).map(([bbType, assets]) => ({ bbType, assets }));

  return assets ? (
    <Stack gap="lg" mt="xl">
      <Box>
        <AllocationBarChart
          items={chartAssets.map(({ name, share }) => ({ name, share }))}
          withLegend={false}
        />
        <Text
          mt="md"
          fw={700}
          fz={isMobile ? "sm" : "md"}
          style={{ wordBreak: "break-word", lineHeight: 1 }}
          className={REPLAY_BLOCK_CLASS}
        >
          {`Totalt värde: ${totalMarketValue.toLocaleString("sv-SE", { style: "currency", currency: "SEK" })}`}
        </Text>
      </Box>
      <Box>
        {groupedByBbType.map((group) => (
          <Box key={group.bbType} style={{ width: "100%" }}>
            <Box
              style={{
                marginBottom: "1rem",
                borderBottom: "1px solid var(--mantine-color-gray-1)",
                paddingBottom: "10px",
              }}
            >
              <Text
                m={0}
                fw={isMobile ? 400 : 700}
                fz={isMobile ? "sm" : "md"}
                style={{
                  wordBreak: "break-word",
                  textAlign: "left",
                  lineHeight: 1,
                }}
              >
                {group.bbType === "ir"
                  ? "Räntor/Obligationer"
                  : group.bbType === "eq"
                    ? "Aktier"
                    : "Alternativa investeringar"}
              </Text>
            </Box>

            <InsuranceAssetList assets={group.assets} />
          </Box>
        ))}
      </Box>
    </Stack>
  ) : null;
};

export default InsuranceAssetOverview;
